import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaGlobeAmericas,
  FaHeart,
  FaMapMarkerAlt,
  FaPlus,
  FaRegCalendarAlt,
  FaShoppingBag,
  FaStore,
  FaTag,
} from "react-icons/fa";
import { useFavorites } from "./FavoritesContext";
import "./HubHero.css";

/* =========================================================
   HubHero v4   manual swipe carousel, skyline card first.

   • Slide 1 is the design-mock card: "Discover local." + two
     pills, San Antonio skyline on the right.
   • Slides 2–5 are the previous hero slides (events, saved,
     grow, HubMart) in the same card style.
   • Swipe / drag, dots, and desktop arrows. NO auto-rotate:
     nothing moves unless the person moves it.

   PICTURES (bundled by webpack from ./heroArt, beside this file):
     sa-skyline.jpg   slide 1 photo
     slide-2.png … slide-5.png   optional art for slides 2–5;
                      a missing file = plain purple card for
                      that slide. (slide-1.png is ignored: the
                      skyline owns slide 1.)
   Each picture is a real <img> sized to the card HEIGHT, never
   stretched to the card width, so it stays sharp on desktop.
   Per-slide knobs: HubHero.css (--sa-pos / --sa-fade / --sa-flip).
   ========================================================= */

const HUBMART_ROUTE = "/HubMart";
const ADD_BUSINESS_ROUTE = "/partner-signup";
const PROFILE_ROUTE = "/profile";

const tryArt = (name, ext) => {
  try {
    /* eslint-disable-next-line global-require, import/no-dynamic-require */
    return require(`./heroArt/${name}.${ext}`);
  } catch {
    return null;
  }
};

const PHOTO_SKYLINE = tryArt("sa-skyline", "jpg");
const ART = {
  2: tryArt("slide-2", "png"),
  3: tryArt("slide-3", "png"),
  4: tryArt("slide-4", "png"),
  5: tryArt("slide-5", "png"),
};

const scrollToSelector = (selector) => {
  const el = document.querySelector(selector);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function Pill({ chip }) {
  const Icon = chip.icon;
  return (
    <button type="button" className={`hhx-pill hhx-pill--${chip.style}`} onClick={chip.run}>
      {Icon && <Icon aria-hidden="true" />}
      {chip.label}
    </button>
  );
}

export default function HubHero({
  title = "Discover local.",
  lede = "Find businesses, events, and deals from people who make our city great.",
}) {
  const navigate = useNavigate();
  const { user, requestSignInPrompt } = useFavorites();
  const railRef = useRef(null);
  const [active, setActive] = useState(0);

  const slides = useMemo(
    () => [
      {
        key: "discover",
        title,
        lede,
        picture: PHOTO_SKYLINE,
        kind: "photo",
        primary: {
          label: "Browse communities",
          icon: FaGlobeAmericas,
          style: "light",
          run: () => scrollToSelector("#hub-panel-community"),
        },
        secondary: {
          label: "Shop HubMart",
          icon: FaShoppingBag,
          style: "glass",
          run: () => navigate(HUBMART_ROUTE),
        },
      },
      {
        key: "events",
        title: "Happening near you.",
        lede: "Festivals, markets, meetups. See what's on this week, or post yours free.",
        picture: ART[2],
        kind: "art",
        primary: {
          label: "Post an event",
          icon: FaPlus,
          style: "gold",
          run: () => window.dispatchEvent(new CustomEvent("gohub:post-event")),
        },
        secondary: {
          label: "See events",
          icon: FaRegCalendarAlt,
          style: "glass",
          run: () => scrollToSelector(".her"),
        },
      },
      {
        key: "saved",
        title: "Make it yours.",
        lede: "Saved places and events wait in your profile, on every device.",
        picture: ART[3],
        kind: "art",
        primary: {
          label: user ? "Your saved ♥" : "Sign up free",
          icon: FaHeart,
          style: "gold",
          run: () =>
            user
              ? navigate(PROFILE_ROUTE)
              : requestSignInPrompt({ kind: "profile", name: "your saved places" }),
        },
        secondary: {
          label: "Browse communities",
          icon: FaGlobeAmericas,
          style: "glass",
          run: () => scrollToSelector("#hub-panel-community"),
        },
      },
      {
        key: "grow",
        title: "Grow with us.",
        lede: "List your business in minutes, and find the city's community organizations in one place.",
        picture: ART[4],
        kind: "art",
        primary: {
          label: "Add your business",
          icon: FaStore,
          style: "gold",
          run: () => navigate(ADD_BUSINESS_ROUTE),
        },
        secondary: {
          label: "Explore community pages",
          icon: FaGlobeAmericas,
          style: "glass",
          run: () => scrollToSelector(".hqa"),
        },
      },
      {
        key: "hubmart",
        title: "Shop HubMart.",
        lede: "Buy and sell nearby: cars, phones, furniture, treasures. Free to list, easy to find.",
        picture: ART[5],
        kind: "art",
        primary: {
          label: "Shop HubMart",
          icon: FaShoppingBag,
          style: "light",
          run: () => navigate(HUBMART_ROUTE),
        },
        secondary: {
          label: "Sell something",
          icon: FaTag,
          style: "glass",
          run: () => navigate(HUBMART_ROUTE),
        },
      },
    ],
    [title, lede, user, navigate, requestSignInPrompt]
  );

  /* which slide is in view (swipe, drag, or arrows) */
  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;

    let raf = 0;
    const onScroll = () => {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const width = el.clientWidth || 1;
        setActive(
          Math.max(0, Math.min(slides.length - 1, Math.round(el.scrollLeft / width)))
        );
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
    };
  }, [slides.length]);

  const goTo = useCallback(
    (index) => {
      const el = railRef.current;
      if (!el) return;
      const next = Math.max(0, Math.min(slides.length - 1, index));
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollTo({ left: next * el.clientWidth, behavior: reduce ? "auto" : "smooth" });
    },
    [slides.length]
  );

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      goTo(active - 1);
    }
  };

  return (
    <section className="hhx" aria-label="Welcome to GoOurHub">
      {/* the city label: a gold ribbon between header and hero */}
      <div className="hhx-ribbon-row">
        <span className="hhx-ribbon">
          <FaMapMarkerAlt aria-hidden="true" />
          <span>
             <b>San Antonio, TX</b>
          </span>
        </span>
      </div>

      <div
        className="hhx-frame"
        role="region"
        aria-roledescription="carousel"
        aria-label="Highlights"
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <div className="hhx-rail" ref={railRef}>
          {slides.map((slide, index) => (
            <article
              key={slide.key}
              className={`hhx-stage hhx-stage--${slide.key}${
                slide.picture ? ` hhx-stage--pic hhx-stage--${slide.kind}` : ""
              }`}
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}: ${slide.title}`}
            >
              {slide.picture && (
                <span className="hhx-photo" aria-hidden="true">
                  <img src={slide.picture} alt="" draggable="false" />
                </span>
              )}

              <div className="hhx-copy">
                {index === 0 ? <h1>{slide.title}</h1> : <h2>{slide.title}</h2>}
                <p>{slide.lede}</p>

                <div className="hhx-cta">
                  <Pill chip={slide.primary} />
                  <Pill chip={slide.secondary} />
                </div>
              </div>
            </article>
          ))}
        </div>

        <button
          type="button"
          className="hhx-arrow hhx-arrow--prev"
          aria-label="Previous slide"
          disabled={active === 0}
          onClick={() => goTo(active - 1)}
        >
          <FaChevronLeft />
        </button>
        <button
          type="button"
          className="hhx-arrow hhx-arrow--next"
          aria-label="Next slide"
          disabled={active === slides.length - 1}
          onClick={() => goTo(active + 1)}
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="hhx-dots" role="tablist" aria-label="Choose a slide">
        {slides.map((slide, index) => (
          <button
            key={slide.key}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-label={`Slide ${index + 1}: ${slide.title}`}
            className={`hhx-dot${index === active ? " is-on" : ""}`}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </section>
  );
}
